import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SellerLayout } from '../components/SellerLayout';
import { ShipmentLabelModal } from '../components/ShipmentLabelModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getShipment, deleteShipment, removeShipmentItem, voidShipmentLabel } from '../api/client';
import { colors } from '../constants/colors';
import { parseUTC } from '../utils/date';
import { Package, ArrowLeft, Truck, Clock, Tag, FileText, Ruler, ExternalLink, Trash2, X, Loader2, CheckCircle, XCircle, ShoppingBag } from 'lucide-react';
import type { Shipment, ShipmentStatus, ItemSaleWithPayment } from '../types';
import { Img } from '../components/Img';

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

function getTrackingUrl(carrier: string, trackingNumber: string): string | null {
  switch (carrier) {
    case 'USPS': return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    case 'UPS': return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    case 'FedEx': return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    default: return null;
  }
}

const STATUS_CONFIG: Record<ShipmentStatus, { label: string; color: string; bgColor: string }> = {
  CREATED: { label: 'Created', color: '#F59E0B', bgColor: '#FEF3C7' },
  LABEL_PURCHASED: { label: 'Label Created', color: '#5856D6', bgColor: '#EDE9FE' },
  IN_TRANSIT: { label: 'In Transit', color: '#007AFF', bgColor: '#DBEAFE' },
  DELIVERED: { label: 'Delivered', color: '#22C55E', bgColor: '#DCFCE7' },
  EXCEPTION: { label: 'Exception', color: '#EF4444', bgColor: '#FEE2E2' },
};

function formatDate(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateString: string): string {
  const date = parseUTC(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export function SellerShipmentDetailPage() {
  useDocumentTitle('Shipment Details');
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [deletedOrderId, setDeletedOrderId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);

  useEffect(() => {
    if (shipmentId) {
      loadShipment(true);
    }
  }, [shipmentId]);

  const loadShipment = async (showLoader = false) => {
    try {
      if (showLoader) setIsLoading(true);
      const data = await getShipment(shipmentId!);
      setShipment(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shipment');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  const handleDeleteShipment = async () => {
    if (!shipmentId || !shipment) return;
    try {
      setIsDeleting(true);
      const orderId = shipment.orderId;
      await deleteShipment(shipmentId);
      setDeletedOrderId(orderId);
      setIsDeleted(true);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete shipment');
      setIsDeleting(false);
    }
  };

  const handleRemoveItem = async (itemSaleId: string) => {
    if (!shipmentId) return;
    try {
      setRemovingItemId(itemSaleId);
      const result = await removeShipmentItem(shipmentId, itemSaleId);
      if (result === null) {
        // Shipment was deleted because it had no more items
        setDeletedOrderId(shipment?.orderId || null);
        setIsDeleted(true);
      } else {
        setShipment(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item');
    } finally {
      setRemovingItemId(null);
    }
  };

  const handleVoidLabel = async () => {
    if (!shipmentId) return;
    try {
      setIsVoiding(true);
      setError(null);
      const updated = await voidShipmentLabel(shipmentId);
      setShipment(updated);
      setShowVoidConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to void label');
    } finally {
      setIsVoiding(false);
    }
  };

  if (isLoading) {
    return (
      <SellerLayout>
        <div style={styles.loading}>Loading shipment...</div>
      </SellerLayout>
    );
  }

  if (isDeleted) {
    return (
      <SellerLayout>
        <div style={styles.deletedScreen}>
          <CheckCircle size={48} color={colors.status.success} />
          <h2 style={styles.deletedTitle}>Shipment Deleted</h2>
          <p style={styles.deletedText}>The shipment has been deleted and its items are now unshipped.</p>
          <button
            style={styles.primaryButton}
            onClick={() => navigate(deletedOrderId ? `/sell/orders/${deletedOrderId}` : '/sell/orders')}
          >
            OK
          </button>
        </div>
      </SellerLayout>
    );
  }

  if (error || !shipment) {
    return (
      <SellerLayout>
        <div style={styles.error}>{error || 'Shipment not found'}</div>
      </SellerLayout>
    );
  }

  const sc = STATUS_CONFIG[shipment.status];
  const canModify = shipment.status === 'CREATED';
  const hasMultipleItems = (shipment.itemSaleCount || 0) > 1;

  return (
    <SellerLayout>
      <button onClick={() => navigate(-1)} style={styles.backButton}>
        <ArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Shipment {shipment.id}</h1>
          <p style={styles.subtitle}>
            Order{' '}
            <span style={styles.linkText} onClick={() => navigate(`/sell/orders/${shipment.orderId}`)}>
              {shipment.orderId}
            </span>
            {' '}&middot;{' '}Created {formatDate(shipment.createdAt)}
          </p>
        </div>
        <div style={{ ...styles.statusBadge, backgroundColor: sc.bgColor, color: sc.color }}>
          {sc.label}
        </div>
      </div>

      <div style={styles.grid}>
        <div style={styles.mainColumn}>
          {/* Items */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Package size={16} />
              Items ({shipment.itemSaleCount})
            </h2>
            <div style={styles.itemsTable}>
              <div style={styles.itemsHeader}>
                <span style={{ flex: 1 }}>Item</span>
                <span style={{ width: '100px' }}>Sale #</span>
                <span style={{ width: '80px', textAlign: 'right' }}>Price</span>
                {canModify && hasMultipleItems && <span style={{ width: '40px' }} />}
              </div>
              {shipment.itemSales?.map((sale: ItemSaleWithPayment) => (
                <div key={sale.id} style={styles.itemRow}>
                  <div style={styles.itemInfo}>
                    {sale.itemSnapshot?.imageUrl ? (
                      <Img src={sale.itemSnapshot.imageUrl} alt={sale.itemSnapshot.name} variant="thumb" renderedWidth={48} blurhash={sale.itemSnapshot.blurhash} style={styles.itemImage} />
                    ) : (
                      <div style={styles.itemImagePlaceholder}>
                        <Package size={20} color={colors.gray[300]} />
                      </div>
                    )}
                    <div>
                      <p style={styles.itemName}>{sale.itemSnapshot?.name ?? 'Unknown Item'}</p>
                      {sale.itemSnapshot?.description && (
                        <p style={styles.itemDescription}>{sale.itemSnapshot.description}</p>
                      )}
                    </div>
                  </div>
                  <span style={styles.saleIdLink} onClick={() => navigate(`/sell/sales/${sale.id}`)}>
                    {sale.id}
                  </span>
                  <span style={styles.itemPrice}>{formatCurrency(sale.winningBid)}</span>
                  {canModify && hasMultipleItems && (
                    <span style={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                      <button
                        style={styles.removeItemButton}
                        onClick={() => handleRemoveItem(sale.id)}
                        disabled={removingItemId !== null}
                        title="Remove from shipment"
                      >
                        {removingItemId === sale.id ? (
                          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        ) : (
                          <X size={14} />
                        )}
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.sideColumn}>
          {/* Tracking */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Truck size={16} />
              Tracking
            </h2>
            {shipment.carrier && shipment.trackingNumber ? (
              <div style={styles.trackingInfo}>
                <div style={styles.carrierBadge}>
                  {CARRIER_LOGOS[shipment.carrier] && (
                    <img src={CARRIER_LOGOS[shipment.carrier]} alt={shipment.carrier} style={styles.carrierLogoSmall} />
                  )}
                  <span>{shipment.carrier}</span>
                </div>
                {(() => {
                  const url = getTrackingUrl(shipment.carrier!, shipment.trackingNumber!);
                  return url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={styles.trackingLink}>
                      {shipment.trackingNumber}
                      <ExternalLink size={13} />
                    </a>
                  ) : (
                    <span style={styles.trackingNumber}>{shipment.trackingNumber}</span>
                  );
                })()}
              </div>
            ) : (
              <p style={styles.noDataText}>No tracking information yet</p>
            )}
          </div>

          {/* Dimensions */}
          {shipment.weightOz != null && (
            <div style={styles.card}>
              <h2 style={styles.sectionTitle}>
                <Ruler size={16} />
                Package
              </h2>
              <div style={styles.detailRow}>
                <span>Weight</span>
                <span>{shipment.weightOz} oz</span>
              </div>
              <div style={styles.detailRow}>
                <span>Dimensions</span>
                <span>{shipment.lengthIn} &times; {shipment.widthIn} &times; {shipment.heightIn} in</span>
              </div>
            </div>
          )}

          {/* Label */}
          {shipment.labelUrl && (
            <div style={styles.card}>
              <h2 style={styles.sectionTitle}>
                <Tag size={16} />
                Label
              </h2>
              {shipment.postageCost != null && (
                <div style={styles.detailRow}>
                  <span>Postage Cost</span>
                  <span style={{ fontWeight: 600 }}>{formatCurrency(shipment.postageCost)}</span>
                </div>
              )}
              <a
                href={shipment.labelUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.downloadLabelButton}
              >
                <FileText size={18} />
                Download Label (PDF)
              </a>
            </div>
          )}

          {/* Buy Label */}
          {shipment.status === 'CREATED' && (
            <button
              style={styles.buyLabelButton}
              onClick={() => setShowLabelModal(true)}
            >
              <ShoppingBag size={16} />
              Buy Shipping Label
            </button>
          )}

          {/* Void Label */}
          {shipment.status === 'LABEL_PURCHASED' && !showVoidConfirm && (
            <button
              style={styles.voidButton}
              onClick={() => setShowVoidConfirm(true)}
            >
              <XCircle size={16} />
              Void Label
            </button>
          )}

          {showVoidConfirm && (
            <div style={styles.card}>
              <p style={styles.voidConfirmText}>
                Void this label? The postage cost ({shipment.postageCost != null ? formatCurrency(shipment.postageCost) : '$0.00'}) will be refunded to your balance.
              </p>
              {error && <p style={styles.confirmError}>{error}</p>}
              <div style={styles.confirmActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => { setShowVoidConfirm(false); setError(null); }}
                  disabled={isVoiding}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.confirmDeleteButton,
                    ...(isVoiding ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={handleVoidLabel}
                  disabled={isVoiding}
                >
                  {isVoiding ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Voiding...
                    </>
                  ) : (
                    'Void Label'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>
              <Clock size={16} />
              Timeline
            </h2>
            <div style={styles.detailRow}>
              <span>Created</span>
              <span>{formatDateTime(shipment.createdAt)}</span>
            </div>
            {shipment.shippedAt && (
              <div style={styles.detailRow}>
                <span>Shipped</span>
                <span>{formatDateTime(shipment.shippedAt)}</span>
              </div>
            )}
            {shipment.deliveredAt && (
              <div style={styles.detailRow}>
                <span>Delivered</span>
                <span>{formatDateTime(shipment.deliveredAt)}</span>
              </div>
            )}
          </div>

          {/* Delete Shipment */}
          {canModify && (
            <button
              style={styles.deleteButton}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={16} />
              Delete Shipment
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.confirmModal}>
            <h3 style={styles.confirmTitle}>Delete Shipment?</h3>
            <p style={styles.confirmText}>
              This will remove all items from this shipment and delete it.
              The items will become unshipped again.
            </p>
            {error && <p style={styles.confirmError}>{error}</p>}
            <div style={styles.confirmActions}>
              <button
                style={styles.cancelButton}
                onClick={() => { setShowDeleteConfirm(false); setError(null); }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.confirmDeleteButton,
                  ...(isDeleting ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleDeleteShipment}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buy Label Modal */}
      {shipment && (
        <ShipmentLabelModal
          isOpen={showLabelModal}
          onClose={() => setShowLabelModal(false)}
          onSuccess={() => loadShipment()}
          shipment={shipment}
        />
      )}
    </SellerLayout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.gray[600],
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: colors.accent.pink,
    backgroundColor: '#FEE2E2',
    borderRadius: '12px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  linkText: {
    color: colors.brand.blue,
    cursor: 'pointer',
    fontFamily: 'monospace',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '8px 14px',
    borderRadius: '100px',
    fontSize: '14px',
    fontWeight: 600,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 320px',
    gap: '20px',
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 16px 0',
  },
  itemsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemsHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  itemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  itemImage: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
  },
  itemImagePlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: colors.gray[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemName: {
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
    margin: 0,
  },
  itemDescription: {
    fontSize: '12px',
    color: colors.gray[600],
    margin: '2px 0 0 0',
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  saleIdLink: {
    width: '100px',
    fontSize: '13px',
    color: colors.brand.blue,
    fontFamily: 'monospace',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  itemPrice: {
    width: '80px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.ink.black,
  },
  trackingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  carrierBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: colors.gray[100],
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[700],
    width: 'fit-content',
  },
  carrierLogoSmall: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  trackingNumber: {
    fontSize: '14px',
    color: colors.ink.black,
    fontFamily: 'monospace',
  },
  trackingLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    color: colors.brand.blue,
    fontFamily: 'monospace',
    textDecoration: 'none',
  },
  noDataText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: 0,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: colors.gray[700],
    marginBottom: '10px',
  },
  downloadLabelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#22C55E',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
    boxSizing: 'border-box',
    marginTop: '8px',
  },
  removeItemButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '6px',
    cursor: 'pointer',
    color: colors.gray[600],
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    maxWidth: '240px',
  },
  deletedScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '64px 24px',
    textAlign: 'center',
  },
  deletedTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: 0,
  },
  deletedText: {
    fontSize: '14px',
    color: colors.gray[600],
    margin: '0 0 8px 0',
    maxWidth: '360px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  confirmModal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    margin: '20px',
  },
  confirmTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
    margin: '0 0 8px 0',
  },
  confirmText: {
    fontSize: '14px',
    color: colors.gray[600],
    lineHeight: 1.5,
    margin: '0 0 20px 0',
  },
  confirmError: {
    color: '#DC2626',
    fontSize: '13px',
    margin: '0 0 12px 0',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  confirmDeleteButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#DC2626',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  buyLabelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  voidButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  voidConfirmText: {
    fontSize: '14px',
    color: colors.gray[700],
    lineHeight: 1.5,
    margin: '0 0 16px 0',
  },
};
